import React, { useContext, useEffect, useState } from 'react';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from '../../bootstrap/Card';
import Page from '../../../layout/Page/Page';
import Button from '../../bootstrap/Button';
import { toasts } from '../../../services/toast.service';
import useDarkMode from '../../../hooks/useDarkMode';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import { getBatchMasterDetails } from '../../../services/master.service';
import { getTransportRangeByBatchMasterId, getTransportRange, updateTransportRangeBatchWise } from '../../../services/transport.service';
import { showLoader } from '../../../services/loader.services';
import { getLicenseKey } from '../../../services/application.settings';
import AuthContext from '../../../contexts/authContext';
import AlertService from '../../../services/AlertService';
import Icon from '../../icon/Icon';
import SubHeader, { SubHeaderLeft, SubHeaderRight, SubheaderSeparator } from '../../../layout/SubHeader/SubHeader';
import SearchableSelect from '../../../common/components/SearchableSelect';

const TransportFees = () => {
    const { userAccountId } = useContext(AuthContext);
    const { darkModeStatus } = useDarkMode();

    // Data states
    const [batchList, setBatchList] = useState<any[]>([]);
    const [transportAreaList, setTransportAreaList] = useState<any[]>([]);
    const [transportRangeList, setTransportRangeList] = useState<any[]>([]);

    // Selection states
    const [selectedBatch, setSelectedBatch] = useState<any>(null);
    const [viewedBatchId, setViewedBatchId] = useState<string>('');
    const [areaMappings, setAreaMappings] = useState<{ [key: number]: string }>({});
    const [originalMappings, setOriginalMappings] = useState<{ [key: number]: string }>({});

    // Alert states
    const [alertStatus, setAlertStatus] = useState<any>({ message: '', type: '' });
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        fetchBatches();
        fetchRanges();
    }, []);

    const fetchBatches = () => {
        showLoader(true);
        getBatchMasterDetails(
            (response) => {
                showLoader(false);
                if (response.data.success) {
                    const data = response.data.data.batchMasterDetails;
                    if (data) {
                        setBatchList(data);
                    } else {
                        toasts('No batches found', 'Error');
                    }
                } else {
                    toasts(response.data.message || 'Failed to fetch batches', 'Error');
                }
            },
            (error) => {
                showLoader(false);
                toasts(error, 'Error');
            }
        );
    };

    const fetchRanges = () => {
        getTransportRange(0,
            (response) => {
                if (response.data.success) {
                    const data = response.data.data.transportRange;
                    if (data) {
                        setTransportRangeList(data);
                    }
                } else {
                    toasts(response.data.message || 'Failed to fetch transport ranges', 'Error');
                }
            },
            (error) => {
                toasts(error, 'Error');
            }
        );
    };

    const fetchAreas = (batchId: string) => {
        showLoader(true);
        getTransportRangeByBatchMasterId(Number(batchId),
            (response) => {
                showLoader(false);
                if (response.data.success) {
                    let data = null;
                    if (response.data.data) {
                        const keys = Object.keys(response.data.data);
                        for (let k of keys) {
                            if (Array.isArray(response.data.data[k])) {
                                data = response.data.data[k];
                                break;
                            }
                        }
                        if (!data) {
                            data = response.data.data.transportAreaMaster || response.data.data.transportRangeByBatchMasterId;
                        }
                    }
                    if (data) {
                        setTransportAreaList(data);
                        // Populate initial mappings
                        const initialMappings: { [key: number]: string } = {};
                        data.forEach((area: any) => {
                            initialMappings[area.transportAreaMasterId] = area.transportRangeId ? area.transportRangeId.toString() : '';
                        });
                        setAreaMappings(initialMappings);
                        setOriginalMappings(initialMappings);
                    }
                } else {
                    toasts(response.data.message || 'Failed to fetch transport areas', 'Error');
                }
            },
            (error) => {
                showLoader(false);
                toasts(error, 'Error');
            }
        );
    };

    const handleView = () => {
        if (!selectedBatch) {
            toasts('Please select a batch first', 'warning');
            return;
        }
        setViewedBatchId(selectedBatch.value);
        fetchAreas(selectedBatch.value);
    };

    const handleRangeChange = (areaId: number, rangeId: string) => {
        setAreaMappings(prev => ({
            ...prev,
            [areaId]: rangeId
        }));
    };

    const getFeeAmount = (rangeId: string) => {
        if (!rangeId) return '0.00';
        const range = transportRangeList.find(r => r.transportRangeId.toString() === rangeId);
        return range ? Number(range.amount).toFixed(2) : '0.00';
    };

    const isDirty = () => {
        return Object.keys(areaMappings).some(key => {
            const numKey = Number(key);
            return areaMappings[numKey] !== originalMappings[numKey];
        });
    };

    const handleSave = () => {
        if (!viewedBatchId) {
            toasts('Please select a batch and click View first', 'error');
            return;
        }

        const rangeArray = transportAreaList.map(area => {
            const rangeIdStr = areaMappings[area.transportAreaMasterId];
            return {
                transportAreaMasterId: area.transportAreaMasterId,
                transportRangeId: rangeIdStr ? Number(rangeIdStr) : 0
            };
        });

        const payload = {
            licenseKey: getLicenseKey,
            batchMasterId: Number(viewedBatchId),
            staffDetailsId: userAccountId,
            rangeArray: rangeArray
        };

        showLoader(true);
        updateTransportRangeBatchWise(payload,
            (response) => {
                showLoader(false);
                if (response.data.success) {
                    setAlertStatus({ message: 'Transport fees updated successfully', type: 'success' });
                    setIsOpen(true);
                    setOriginalMappings({ ...areaMappings });
                    fetchAreas(viewedBatchId);
                } else {
                    setAlertStatus({ message: response.data.message || 'Update failed', type: 'error' });
                    setIsOpen(true);
                }
            },
            (error) => {
                showLoader(false);
                setAlertStatus({ message: error.toString(), type: 'error' });
                setIsOpen(true);
            }
        );
    };

    const getViewedBatchName = () => {
        const batch = batchList.find(b => b.batchMasterId.toString() === viewedBatchId);
        return batch ? batch.batchName : viewedBatchId;
    };

    return (
        <PageWrapper title='Transport Fees'>
            <SubHeader>
                <SubHeaderLeft>
                    {viewedBatchId ? (
                        <span className='fs-6 fw-bold text-muted'>
                            Configuring fees for batch: {getViewedBatchName()}
                        </span>
                    ) : (
                        <span className='fs-6 fw-bold text-muted'>
                            Select a batch and click View to get started
                        </span>
                    )}
                </SubHeaderLeft>
                <SubHeaderRight>
                    <SubheaderSeparator />
                    <Button
                        icon='Save'
                        color='info'
                        isDisable={!viewedBatchId || !isDirty()}
                        onClick={handleSave}
                    >
                        Save Changes
                    </Button>
                </SubHeaderRight>
            </SubHeader>
            <Page container='fluid'>
                <div className='row px-4'>
                    <div className='col-12'>
                        <Card stretch>
                            <CardHeader borderSize={1}>
                                <CardLabel icon='LocalShipping' iconColor='info'>
                                    <CardTitle tag='h4'>
                                        Transport Fees Allocation
                                    </CardTitle>
                                </CardLabel>
                                <div className='d-flex align-items-center' style={{ minWidth: '350px' }}>
                                    <span className='me-2 fw-bold text-nowrap'>Batch:</span>
                                    <div className='me-2 flex-grow-1' style={{ minWidth: '180px' }}>
                                        <SearchableSelect
                                            ariaLabel='Select Batch'
                                            placeholder='Select Batch'
                                            value={selectedBatch}
                                            onChange={(e: any) => setSelectedBatch(e)}
                                            list={batchList.map((batch: any) => ({
                                                value: batch.batchMasterId.toString(),
                                                label: batch.batchName
                                            }))}
                                        />
                                    </div>
                                    <Button
                                        color='info'
                                        icon='Visibility'
                                        isDisable={!selectedBatch}
                                        onClick={handleView}
                                    >
                                        View
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                {viewedBatchId ? (
                                    <div className='table-responsive'>
                                        <table className='table table-modern table-hover text-nowrap align-middle'>
                                            <thead>
                                                <tr className='table-light fs-6'>
                                                    <th className='text-center' style={{ width: '80px' }}>S.No</th>
                                                    <th>Area Name</th>
                                                    <th>Transport Range Selection</th>
                                                    <th className='text-end' style={{ width: '200px' }}>Fee Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {transportAreaList.length > 0 ? (
                                                    transportAreaList.map((area: any, index: number) => {
                                                        const currentRangeId = areaMappings[area.transportAreaMasterId] || '';
                                                        const currentRange = transportRangeList.find(r => r.transportRangeId.toString() === currentRangeId);
                                                        const selectValue = currentRange ? { value: currentRangeId, label: currentRange.rangeName } : null;
                                                        return (
                                                            <tr key={area.transportAreaMasterId}>
                                                                <td className='text-center text-muted'>{index + 1}</td>
                                                                <td className='fw-bold text-dark'>{area.areaName}</td>
                                                                <td>
                                                                    <div style={{ maxWidth: '320px' }}>
                                                                        <SearchableSelect
                                                                            ariaLabel='Select Transport Range'
                                                                            placeholder='Select Range'
                                                                            value={selectValue}
                                                                            onChange={(e: any) => handleRangeChange(area.transportAreaMasterId, e ? e.value : '')}
                                                                            list={transportRangeList.map((range: any) => ({
                                                                                value: range.transportRangeId.toString(),
                                                                                label: range.rangeName
                                                                            }))}
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td className='text-end fw-bold text-info fs-5 pe-4'>
                                                                    ₹ {getFeeAmount(currentRangeId)}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan={4} className='text-center text-muted py-4'>
                                                            No transport areas found.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className='d-flex flex-column align-items-center justify-content-center py-5 text-muted'>
                                        <Icon icon='DirectionsBus' size='4x' className='mb-3 text-info opacity-50' />
                                        <h5 className='fw-bold'>No Batch Loaded</h5>
                                        <p className='text-muted small'>Select a batch from the dropdown above and click the View button to manage transport ranges and fees.</p>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </Page>
            <AlertService setIsOpen={setIsOpen} isOpen={isOpen} alertStatus={alertStatus} isNavigate={null} />
        </PageWrapper>
    );
};

export default TransportFees;